import { API_BASE } from './env';

/**
 * Унифицированный формат ответа от api.* (PostgreSQL → REST):
 *   успех:  { ok: true,  data: ... }
 *   ошибка: { ok: false, error: 'code', message: '...' }
 */
export type ApiOk<T> = { ok: true; data: T };
export type ApiErr = { ok: false; error: string; message?: string };
export type ApiResult<T = unknown> = ApiOk<T> | ApiErr;

export class ApiError extends Error {
  /**
   * `details` — весь ответ ошибки как есть. Нужен, когда endpoint отдаёт
   * машиночитаемые поля рядом с error/message (например `granted_at` у
   * `already_granted`), чтобы UI показал их на своём языке.
   */
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const CSRF_COOKIE = 'lms_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]!) : null;
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Загрузка FormData с прогрессом. fetch не умеет отдавать upload-progress,
 * поэтому здесь XMLHttpRequest. Для файлов до 2 GB без прогресса не обойтись.
 * onProgress получает долю 0..1 отправленных байт.
 */
export function apiUpload<T = unknown>(
  path: string,
  body: FormData,
  opts: { signal?: AbortSignal; onProgress?: (fraction: number) => void } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', new URL((API_BASE || '') + path, window.location.origin).toString());
    xhr.withCredentials = true;
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) xhr.setRequestHeader(CSRF_HEADER, csrf);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      let json: ApiResult<T> | null = null;
      try {
        json = JSON.parse(xhr.responseText) as ApiResult<T>;
      } catch {
        // nginx может вернуть HTML (413/504) — обработаем ниже
      }
      if (xhr.status < 400 && json && json.ok) {
        resolve(json.data);
        return;
      }
      if (xhr.status === 401 && !window.location.pathname.startsWith('/login')) {
        window.location.assign(
          '/login?from=' + encodeURIComponent(window.location.pathname + window.location.search),
        );
      }
      const err = json && !json.ok ? json : null;
      reject(
        new ApiError(
          err?.error ?? `http_${xhr.status}`,
          err?.message ?? `HTTP ${xhr.status}`,
          xhr.status,
          (err ?? undefined) as Record<string, unknown> | undefined,
        ),
      );
    };
    xhr.onerror = () => reject(new ApiError('network_error', 'Network error', 0));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));

    if (opts.signal) {
      if (opts.signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(body);
  });
}

/**
 * Базовый fetch-обёртка для всех вызовов /api/admin/*.
 * - credentials: include — для куков (JWT, CSRF)
 * - на POST/PATCH/DELETE добавляет X-CSRF-Token из cookie
 * - сериализует body в JSON, парсит ответ
 * - throws ApiError если HTTP не ok или ok:false
 */
export async function apiFetch<T = unknown>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const url = new URL((API_BASE || '') + path, window.location.origin);
  if (options.query) {
    Object.entries(options.query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    });
  }

  const method = (options.method ?? 'GET').toUpperCase();
  const isStateChanging = method !== 'GET' && method !== 'HEAD';

  const headers = new Headers(options.headers);
  let body: BodyInit | undefined;

  if (options.body !== undefined && options.body !== null) {
    if (options.body instanceof FormData || options.body instanceof Blob) {
      body = options.body as BodyInit;
    } else {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(options.body);
    }
  }

  if (isStateChanging) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }

  const resp = await fetch(url.toString(), {
    ...options,
    method,
    headers,
    body,
    credentials: 'include',
  });

  // Receipt download и другие бинарные ответы — возвращаем сырой Response
  const contentType = resp.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (!resp.ok) {
      throw new ApiError(`http_${resp.status}`, `HTTP ${resp.status}`, resp.status);
    }
    return resp as unknown as T;
  }

  const json = (await resp.json()) as ApiResult<T> | T;

  if (resp.status >= 400 || (typeof json === 'object' && json && 'ok' in json && (json as ApiResult).ok === false)) {
    const errObj = json as ApiErr;
    // 401 / отозванная сессия → редирект на /login (кроме самой страницы login)
    if (
      resp.status === 401 &&
      typeof window !== 'undefined' &&
      !window.location.pathname.startsWith('/login')
    ) {
      const from = window.location.pathname + window.location.search;
      window.location.assign('/login?from=' + encodeURIComponent(from));
    }
    throw new ApiError(
      errObj.error ?? `http_${resp.status}`,
      errObj.message ?? resp.statusText,
      resp.status,
      errObj as unknown as Record<string, unknown>,
    );
  }

  if (typeof json === 'object' && json && 'ok' in json && (json as ApiResult).ok === true) {
    return (json as ApiOk<T>).data;
  }
  return json as T;
}
